import { useState } from 'react';
import { differenceInMinutes, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Icon, Pencil, Trash2, Bell, Plane, Paperclip, MapPin, ChevronDown } from '../../ui/Icon';
import { fileIcon } from './documentFiles';
import { TicketExpenseActions, TicketExpenseBadge } from './TicketExpenses';
import { flightOption } from '../../../utils/reservations';
import { UserAvatar } from '../../ui/UserAvatar';
import type { Flight, FlightDocument, FlightParticipant } from '../../../types';
import { fmtUTCTime, fmtUTCDate, fmtUTCDateTime } from '../../../utils/dateTime';
import { useDateLocale } from '../../../utils/dateLocale';

interface Props {
  flight: Flight;
  docs?: FlightDocument[];
  onEdit: () => void;
  onDelete: () => void;
}

const R2_PUBLIC_URL = (import.meta.env.VITE_R2_PUBLIC_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const fileUrl = (filename: string) => `${R2_PUBLIC_URL}/${filename}`;

function formatLayover(arrStr: string, depStr: string): string {
  try {
    const mins = differenceInMinutes(parseISO(depStr), parseISO(arrStr));
    if (mins < 0) return '';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  } catch {
    return '';
  }
}

function formatDuration(depStr: string, arrStr: string): string {
  try {
    const mins = differenceInMinutes(parseISO(arrStr), parseISO(depStr));
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  } catch {
    return '';
  }
}

interface Leg {
  from: string;
  to: string;
  depTime: string;
  arrTime: string;
  flightNumber: string | null;
  airline: string | null;
  reminderMinutes: number | null;
  layoverAt: string | null;
  layoverArr: string | null;
  layoverDep: string | null;
}

function buildLegs(flight: Flight): Leg[] {
  const stops = flight.stops ?? [];
  return stops.map((s, i) => {
    const next = stops[i + 1];
    return {
      from:            s.fromAirport,
      to:              s.toAirport,
      depTime:         s.departureTime,
      arrTime:         s.arrivalTime,
      flightNumber:    s.flightNumber,
      airline:         s.airline,
      reminderMinutes: s.reminderMinutes,
      layoverAt:    next ? s.toAirport : null,
      layoverArr:   next ? s.arrivalTime : null,
      layoverDep:   next ? next.departureTime : null,
    };
  });
}

export function FlightTicket({ flight, docs = [], onEdit, onDelete }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const [open, setOpen] = useState(false);
  const isOutbound = flight.direction === 'OUTBOUND';
  const reservation = flightOption(flight);
  const duration = formatDuration(flight.departureTime, flight.arrivalTime);
  const stops = flight.stops ?? [];
  const legs = stops.length > 0 ? buildLegs(flight) : [];
  const layovers = stops.length > 1 ? stops.length - 1 : 0;

  const allAirlines = stops.length > 0
    ? [...new Set(stops.map(s => s.airline).filter(Boolean) as string[])]
    : flight.airline ? [flight.airline] : [];
  const allFlightNumbers = stops.length > 0
    ? stops.map(s => s.flightNumber).filter(Boolean) as string[]
    : flight.flightNumber ? [flight.flightNumber] : [];

  const participantAvatars = (flight.participants ?? []).map((p: FlightParticipant) => {
    const name = p.user.displayName || p.user.username;
    return <UserAvatar key={p.userId} name={name} avatarUrl={p.user.avatarUrl} size={20} className="ring-1 ring-white dark:ring-slate-900" />;
  });

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-card hover:shadow-hero hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-150">

      {/* ── Header ── */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-left cursor-pointer"
      >
        {/* ── Mobile layout ── */}
        <div className="md:hidden flex items-stretch divide-x divide-dashed divide-slate-200 dark:divide-slate-700">
          {/* Icon strip */}
          <div className={`w-12 shrink-0 flex flex-col items-center justify-center px-2 py-3 gap-1.5 ${isOutbound ? 'bg-sky-50/70 dark:bg-sky-900/15' : 'bg-indigo-50/70 dark:bg-indigo-900/15'}`}>
            <Icon as={Plane} size={18} className={isOutbound ? 'text-sky-500 dark:text-sky-400' : 'text-indigo-500 dark:text-indigo-400 rotate-180'} />
            <span className="font-mono text-[9px] tracking-widest uppercase text-slate-500 dark:text-slate-400 font-semibold"
                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
              {isOutbound ? 'out' : 'ret'}
            </span>
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0 px-4 py-3.5">
            {/* FROM → TO */}
            <div className="flex items-center gap-2">
              <span className="font-serif font-bold text-[24px] leading-none tabular-nums text-slate-900 dark:text-slate-100"
                    style={{  }}>
                {flight.departureAirport?.slice(0, 3).toUpperCase() ?? '???'}
              </span>
              <div className="flex-1 flex items-center gap-1 mx-1">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                <Icon as={Plane} size={11} className="text-slate-300 dark:text-slate-600" />
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
              </div>
              <span className="font-serif font-bold text-[24px] leading-none tabular-nums text-slate-900 dark:text-slate-100"
                    style={{  }}>
                {flight.arrivalAirport?.slice(0, 3).toUpperCase() ?? '???'}
              </span>
            </div>

            {/* Times + duration */}
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {flight.departureTime && (
                <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">{fmtUTCDateTime(flight.departureTime, 'dd MMM', dateLocale)}</span>
              )}
              {duration && <span className="font-mono text-[10px] text-slate-300 dark:text-slate-600">·</span>}
              {duration && <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 tabular-nums">{duration}</span>}
              {layovers > 0 && (
                <span className="font-mono text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400 font-semibold">
                  {t('bookings.ticket.layover', { count: layovers })}
                </span>
              )}
            </div>

            {/* Airline + flight numbers + notes */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {allAirlines.length > 0 && (
                <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">{allAirlines.join(' · ')}</span>
              )}
              {allFlightNumbers.length > 0 && (
                <span className="font-mono text-[11px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded font-semibold tabular-nums">
                  {allFlightNumbers.join(' · ')}
                </span>
              )}
              <TicketExpenseBadge link={reservation.link} />
              {participantAvatars.length > 0 && <div className="flex items-center gap-0.5">{participantAvatars}</div>}
            </div>
            {flight.notes && (
              <p className="font-mono text-[11px] text-slate-400 dark:text-slate-500 mt-1.5 line-clamp-1 break-words">{flight.notes}</p>
            )}
          </div>

          {/* Chevron */}
          <div className="flex items-center px-3">
            <Icon as={ChevronDown} size={16} className={`text-slate-300 dark:text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </div>

        {/* ── Desktop layout (md+) ── */}
        <div className="hidden md:grid md:grid-cols-[56px_1.1fr_104px_1.1fr_1.2fr] divide-x divide-dashed divide-slate-200 dark:divide-slate-700">
          {/* Col 1: Type indicator */}
          <div className={`flex flex-col items-center justify-center px-2 py-4 gap-2 ${isOutbound ? 'bg-sky-50/70 dark:bg-sky-900/15' : 'bg-indigo-50/70 dark:bg-indigo-900/15'}`}>
            <Icon as={Plane} size={20} className={isOutbound ? 'text-sky-500 dark:text-sky-400' : 'text-indigo-500 dark:text-indigo-400 rotate-180'} />
            <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500 dark:text-slate-400 font-semibold"
                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
              {isOutbound ? t('bookings.status.outbound') : t('bookings.status.return')}
            </span>
          </div>

          {/* Col 2: FROM */}
          <div className="flex flex-col justify-center px-5 py-5">
            <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 mb-2">{t('bookings.ticket.from')}</span>
            <span className="font-serif font-bold text-[34px] leading-none tabular-nums text-slate-900 dark:text-slate-100"
                  style={{  }}>
              {flight.departureAirport?.slice(0, 3).toUpperCase() ?? '???'}
            </span>
            {flight.departureTime && (
              <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 mt-2 tabular-nums">
                {fmtUTCDate(flight.departureTime, 'dd MMM', dateLocale)} · {fmtUTCTime(flight.departureTime)}
              </span>
            )}
          </div>

          {/* Col 3: Duration */}
          <div className="flex flex-col items-center justify-center px-2 py-5 gap-1.5">
            {duration && <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 tabular-nums font-semibold">{duration}</span>}
            <div className="w-full flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700 relative">
                {layovers > 0 && (
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-amber-500" />
                )}
              </div>
              <Icon as={Plane} size={14} className="text-slate-400 dark:text-slate-500" />
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
              <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
            </div>
            {layovers > 0 ? (
              <span className="font-mono text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-semibold">
                {layovers} {layovers === 1 ? 'escala' : 'escalas'}
              </span>
            ) : (
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{t('bookings.ticket.direct')}</span>
            )}
          </div>

          {/* Col 4: TO */}
          <div className="flex flex-col justify-center px-5 py-5">
            <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 mb-2">{t('bookings.ticket.to')}</span>
            <span className="font-serif font-bold text-[34px] leading-none tabular-nums text-slate-900 dark:text-slate-100"
                  style={{  }}>
              {flight.arrivalAirport?.slice(0, 3).toUpperCase() ?? '???'}
            </span>
            {flight.arrivalTime && (
              <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 mt-2 tabular-nums">
                {fmtUTCDate(flight.arrivalTime, 'dd MMM', dateLocale)} · {fmtUTCTime(flight.arrivalTime)}
              </span>
            )}
          </div>

          {/* Col 5: Meta */}
          <div className="flex flex-col justify-center px-5 py-5 gap-2 min-w-0">
            {allAirlines.length > 0 && (
              <span className="font-mono text-[12px] text-slate-600 dark:text-slate-300 truncate font-medium">{allAirlines.join(' · ')}</span>
            )}
            {allFlightNumbers.length > 0 && (
              <span className="font-mono text-[12px] bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded-md w-fit font-semibold tabular-nums truncate max-w-full">
                {allFlightNumbers.join(' · ')}
              </span>
            )}
            {flight.notes && (
              <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500 line-clamp-2 whitespace-pre-wrap break-words">{flight.notes}</span>
            )}
            <TicketExpenseBadge link={reservation.link} size={11} />
            {participantAvatars.length > 0 && <div className="flex items-center gap-0.5 flex-wrap">{participantAvatars}</div>}
            <Icon as={ChevronDown} size={16}
              className={`mt-auto text-slate-300 dark:text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </button>

      {/* ── Expanded content ── */}
      {open && (
        <>
          {/* Legs detail */}
          {legs.length > 0 && (
            <div className="border-t border-dashed border-slate-200 dark:border-slate-700 bg-stone-50/60 dark:bg-slate-800/40">
              {legs.map((leg, i) => (
                <div key={i}>
                  {/* Mobile leg row */}
                  <div className="md:hidden flex items-center gap-3 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-serif font-bold text-[20px] leading-none tabular-nums text-slate-800 dark:text-slate-100"
                              style={{  }}>
                          {leg.from?.slice(0, 3).toUpperCase()}
                        </span>
                        <div className="flex-1 flex items-center gap-1">
                          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                          <Icon as={Plane} size={11} className="text-slate-300 dark:text-slate-600" />
                          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                        </div>
                        <span className="font-serif font-bold text-[20px] leading-none tabular-nums text-slate-800 dark:text-slate-100"
                              style={{  }}>
                          {leg.to?.slice(0, 3).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {leg.depTime && <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 tabular-nums">{fmtUTCTime(leg.depTime)}</span>}
                        {leg.depTime && leg.arrTime && <span className="font-mono text-[10px] text-slate-300 dark:text-slate-600">·</span>}
                        {leg.depTime && leg.arrTime && <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 tabular-nums font-semibold">{formatDuration(leg.depTime, leg.arrTime)}</span>}
                        {leg.arrTime && <span className="font-mono text-[10px] text-slate-300 dark:text-slate-600">·</span>}
                        {leg.arrTime && <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 tabular-nums">{fmtUTCTime(leg.arrTime)}</span>}
                      </div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1">
                      {leg.airline && <span className="font-mono text-[11px] text-slate-600 dark:text-slate-300 font-medium">{leg.airline}</span>}
                      {leg.flightNumber && (
                        <span className="font-mono text-[11px] bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-1.5 py-0.5 rounded font-semibold tabular-nums">
                          {leg.flightNumber}
                        </span>
                      )}
                      {leg.reminderMinutes && (
                        <span className="flex items-center gap-1 font-mono text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
                          <Icon as={Bell} size={10} />
                          {leg.reminderMinutes >= 60 ? `${leg.reminderMinutes / 60}h` : `${leg.reminderMinutes}min`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Desktop leg row — same 5-col grid as parent */}
                  <div className="hidden md:grid md:grid-cols-[56px_1.1fr_104px_1.1fr_1.2fr] divide-x divide-dashed divide-slate-200 dark:divide-slate-700">
                    <div className="bg-stone-50/80 dark:bg-slate-800/40" />
                    <div className="flex flex-col justify-center px-5 py-3">
                      <span className="font-serif font-bold text-[24px] leading-none tabular-nums text-slate-800 dark:text-slate-100"
                            style={{  }}>
                        {leg.from?.slice(0, 3).toUpperCase() ?? '???'}
                      </span>
                      {leg.depTime && (
                        <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 tabular-nums">{fmtUTCDate(leg.depTime, 'dd MMM', dateLocale)} · {fmtUTCTime(leg.depTime)}</span>
                      )}
                    </div>
                    <div className="flex flex-col items-center justify-center px-2 py-3 gap-1.5">
                      {leg.depTime && leg.arrTime && (
                        <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 tabular-nums font-semibold">{formatDuration(leg.depTime, leg.arrTime)}</span>
                      )}
                      <div className="w-full flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
                        <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                        <Icon as={Plane} size={12} className="text-slate-400 dark:text-slate-500" />
                        <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                        <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
                      </div>
                    </div>
                    <div className="flex flex-col justify-center px-5 py-3">
                      <span className="font-serif font-bold text-[24px] leading-none tabular-nums text-slate-800 dark:text-slate-100"
                            style={{  }}>
                        {leg.to?.slice(0, 3).toUpperCase() ?? '???'}
                      </span>
                      {leg.arrTime && (
                        <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 tabular-nums">{fmtUTCDate(leg.arrTime, 'dd MMM', dateLocale)} · {fmtUTCTime(leg.arrTime)}</span>
                      )}
                    </div>
                    <div className="flex flex-col items-start justify-center gap-1.5 px-5 py-3">
                      {leg.airline && <span className="font-mono text-[12px] text-slate-600 dark:text-slate-300 font-medium truncate max-w-full">{leg.airline}</span>}
                      {leg.flightNumber && (
                        <span className="font-mono text-[12px] bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded-md font-semibold tabular-nums">
                          {leg.flightNumber}
                        </span>
                      )}
                      {leg.reminderMinutes && (
                        <span className="flex items-center gap-1.5 font-mono text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
                          <Icon as={Bell} size={11} />
                          {leg.reminderMinutes >= 60 ? `${leg.reminderMinutes / 60}h` : `${leg.reminderMinutes}min`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Layover separator */}
                  {leg.layoverAt && leg.layoverArr && leg.layoverDep && (
                    <div className="flex items-center gap-2 px-5 py-2.5 border-t border-dashed border-slate-200 dark:border-slate-700 bg-amber-50/60 dark:bg-amber-900/10">
                      <Icon as={MapPin} size={13} className="text-amber-500 dark:text-amber-400 shrink-0" />
                      <span className="font-mono text-[12px] text-amber-700 dark:text-amber-400 font-semibold tracking-wide">
                        {t('bookings.ticket.layoverAt', { airport: leg.layoverAt.slice(0, 3).toUpperCase() })}
                        {formatLayover(leg.layoverArr, leg.layoverDep) && (
                          <span className="text-amber-600 dark:text-amber-500 font-medium"> · {formatLayover(leg.layoverArr, leg.layoverDep)}</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Documents */}
          {docs.length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-800/30 px-4 py-2 flex items-center gap-2 flex-wrap">
              <Icon as={Paperclip} size={11} className="text-slate-400 dark:text-slate-500 shrink-0" />
              {docs.map(doc => (
                <a key={doc.id} href={fileUrl(doc.filename)} target="_blank" rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-[10px] font-medium text-teal-600 dark:text-teal-400 hover:border-teal-300 dark:hover:border-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors max-w-[180px]"
                  title={doc.originalName}>
                  <Icon as={fileIcon(doc.mimeType)} size={10} className="shrink-0" />
                  <span className="truncate">{doc.originalName}</span>
                </a>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-2 flex items-center justify-end gap-1">
            <TicketExpenseActions reservation={reservation} />
            <button onClick={e => { e.stopPropagation(); onEdit(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer">
              <Icon as={Pencil} size={12} /> Editar
            </button>
            <button onClick={e => { e.stopPropagation(); onDelete(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 transition-colors cursor-pointer">
              <Icon as={Trash2} size={12} /> Eliminar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
